import { useDispatch } from 'react-redux';
import { Message } from '@blink/components/src/constants/messages';
import { WalletHelper } from '@blink/components/src/utils';
import Logger from '@blink/components/src/utils/logger';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useEthers } from '@/shared/hooks/useEthers';
import { toast } from '@/shared/hooks/useToast';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { getAssetsToSupplyAction, getUsersSupplies } from '@/shared/store/supplies/actions';
import { depositErrorAction } from '@/shared/store/errors/actions';

type MakeSupplylProps = {
    amount: string;
    agreementAddress: string;
    tokenAddress: string;
};

export const useSupplyLpToken = () => {
    const dispatch = useDispatch();
    const { depositLpToken } = useEthers();

    const handleError = (e: any) => {
        let description = Message.SOMETHING_WENT_WRONG;
        let modalType = OperationStatusType.Error;

        if (e?.code === 'ACTION_REJECTED' || e?.message?.includes('Cancelled')) {
            modalType = OperationStatusType.Cancelled;
            description = Message.TRANSACTION_CANCELLED;
        }

        if (e?.message?.includes('Request expired')) {
            modalType = OperationStatusType.Timeout;
            description = Message.REQUEST_EXPIRED;
        }

        Logger.error(`useSupplyLpToken:::${description}`, { error: e });

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: modalType,
                    description,
                },
            }),
        );
    };

    const makeSupply = async ({ amount, agreementAddress, tokenAddress }: MakeSupplylProps) => {
        const commonVariables = {
            amount: amount?.toString() || '0',
            agreementAddress,
            tokenAddress,
            orderPlacedCallback: () =>
                dispatch(
                    showModalAction({
                        modalName: ModalName.OPERATION_STATUS_MODAL,
                        modalProps: {
                            type: OperationStatusType.Success,
                            description: Message.SUPPLY_ORDER_PLACED,
                        },
                    }),
                ),
            receiptReceivedCallback: (data: any) => {
                if (data.status === 1) {
                    toast({
                        title: Message.TRANSACTION_COMPLETED,
                        variant: 'success',
                    });
                    dispatch(getUsersSupplies());
                    dispatch(getAssetsToSupplyAction());

                    return;
                }

                Logger.error('useSupplyLpToken:::deposit:makeSupply', { error: data });

                throw data.error || Message.SOMETHING_WENT_WRONG;
            },
        };

        try {
            await depositLpToken(commonVariables);
        } catch (e: any) {
            const badMessage = WalletHelper.messageInCaseOfNotCorrectNetwork(e);

            dispatch(
                depositErrorAction({
                    message: badMessage || Message.SOMETHING_WENT_WRONG,
                }),
            );

            handleError(e);
        }
    };

    return {
        makeSupply,
    };
};
